import React, { useRef, useState } from "react";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import useTabId from "hooks/dashboard/useTabId";
import { ProgressSpinner } from "primereact/progressspinner";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import useDashboardStore from "store/dashboard-store";
import "../dashboard-styles/DashboardMain.scss";
import {
  DashboardLayoutType,
  IDashboardComponent,
  OldDashboardComponent,
} from "api/model/dashboard/DashboardComponent";
import { getDefaultLayout } from "./layouts/helpers";
import ImportDashboardOrTab, { handleAddNewTab } from "./ImportDashboardOrTab";
import { onDragEndSplice, updateDashboardTabs } from "../utils";

export interface ICurrentDashboard {
  dashboard: Dashboard;
  isEditMode: boolean;
  setCurrentDashboard: (dashboard: Dashboard) => void;
}

const initialNewGroupTitle = "New Group";

const DashboardMain = () => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const setIsEditMode = useDashboardStore(state => state.setIsEditMode);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const tabId = useTabId();
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);
  const isComponentsReorderEnabled = useDashboardStore(state => state.isComponentsReorderEnabled);

  const navigate = useNavigate();

  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [isImportDashboardDialogVisible, setIsImportDashboardDialogVisibility] =
    useState<boolean>(false);
  const [selectedTabTitle, setSelectedTabTitle] = useState<string>("");
  const [reorderTabsDialogVisibility, setReorderTabsDialogVisibility] = useState<boolean>(false);
  const [addNewGroupDialogVisibility, setAddNewGroupDialogVisibility] = useState<boolean>(false);
  const [newGroupTitle, setNewGroupTitle] = useState<string>(initialNewGroupTitle);
  const menuRef = useRef<Menu>(null);

  const menuItems = dashboard?.dashboardTabsJs?.map((tab, index) => ({
    label: tab.title,
    command: () => onTabChange(index),
  }));

  const onTabChange = (index: number) => {
    if (index !== tabId) {
      navigate(`/dashboard/${dashboard?.id}/tab/${index}`);
    }
  };

  const handleEditMode = (e: InputSwitchChangeParams) => {
    if (!e.value) {
      setIsGroupReorderEnabled(e.value);
    }
    setIsEditMode(e.value);
  };
  const onDragEnd = (result: any) => {
    if (!result.destination || !dashboard) return;

    const reorderedTabs = onDragEndSplice(result, dashboard.dashboardTabsJs);

    updateDashboardTabs(dashboard, reorderedTabs);

    onTabChange(0);
  };

  const updateQuery = useUpdateDashboard({
    onSuccess: () => {
      easasAlert({
        summary: "Success",
        detail: "Dashboard updated",
        severity: "success",
        life: 3000,
      });
    },
    onFailure: (error: Error) => {
      easasAlert({ summary: "Error", detail: error.message, severity: "error", life: 3000 });
    },
  });

  if (!dashboard) {
    return <ProgressSpinner />;
  }

  const importNewDashboard = (newDashboard: Dashboard) => {
    const newTabs = newDashboard.dashboardTabs.map(tab => {
      const components: IDashboardComponent[] = tab.components.map(
        (component: OldDashboardComponent) => {
          if (component.TYPE !== "ADVANCED_COMPONENT") {
            return {
              id: component.id,
              title: "Not Supported",
              type: "NOT_SUPPORTED",
              config: {},
              layout: {
                HORIZONTAL_FLEX: {
                  width: 12,
                  height: 100,
                },
              },
            };
          }

          const currentLayout = JSON.parse(
            JSON.stringify(getDefaultLayout(DashboardLayoutType.HORIZONTAL_FLEX))
          );

          if (currentLayout && currentLayout.HORIZONTAL_FLEX) {
            currentLayout.HORIZONTAL_FLEX.width =
              component.width || currentLayout.HORIZONTAL_FLEX.width;
          }

          return {
            id: component.id,
            title: component.title || "New Component",
            type: component.componentType,
            config: component.jsonConfig ? JSON.parse(component.jsonConfig) : {},
            layout: currentLayout,
          };
        }
      );

      return {
        uuid: tab.uuid || uuidv4(),
        title: tab.title || "New Tab",
        componentGroups: [
          {
            id: uuidv4(),
            title: tab.title || "New Group",
            layout: {
              type: DashboardLayoutType.HORIZONTAL_FLEX,
            },
            components,
          },
        ],
      };
    });

    updateDashboardTabs(dashboard, newTabs);
  };

  const triggerTabTitleChangeDialog = () => {
    setSelectedTabTitle(dashboard.dashboardTabsJs[tabId].title);
    setIsDialogVisible(true);
  };

  const handleHideDialog = () => {
    setIsDialogVisible(false);
  };

  const removeTabConfirmation = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: "Confirm Action",
      confirmedCallback: handleRemoveSelectedTab,
    });
  };

  const handleRemoveSelectedTab = () => {
    updateDashboardTabs(dashboard, [
      ...dashboard.dashboardTabsJs.slice(0, tabId),
      ...dashboard.dashboardTabsJs.slice(tabId + 1),
    ]);

    navigate(`/dashboard/${dashboard?.id}/tab/0`);
  };

  const handleAddNewTabGroup = (groupTitle: string) => {
    setAddNewGroupDialogVisibility(false);
    setNewGroupTitle(initialNewGroupTitle);

    const currentTabIndex = tabId;
    const currentComponentGroups = dashboard.dashboardTabsJs[currentTabIndex].componentGroups;

    const newComponentGroup = {
      id: uuidv4(),
      title: groupTitle,
      layout: {
        type: DashboardLayoutType.HORIZONTAL_FLEX,
      },
      components: [],
    };

    const updatedComponentGroups = [...currentComponentGroups, newComponentGroup];
    const updatedCurrentDashboard = updateDashboardTabs(
      dashboard,
      dashboard.dashboardTabsJs.map((tab, index) => {
        if (index === currentTabIndex) {
          return {
            ...tab,
            componentGroups: updatedComponentGroups,
          };
        }
        return tab;
      })
    );

    updateQuery.mutate(updatedCurrentDashboard);
  };

  const titleChangeFooter = () => {
    return (
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={handleHideDialog}
          className="p-button-sm p-button-danger"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={() => {
            updateDashboardTabs(dashboard, [
              ...dashboard.dashboardTabsJs.slice(0, tabId),
              {
                ...dashboard.dashboardTabsJs[tabId],
                title: selectedTabTitle,
              },
              ...dashboard.dashboardTabsJs.slice(tabId + 1),
            ]);

            handleHideDialog();
          }}
          className="p-button-sm p-button-success"
        />
      </div>
    );
  };

  const renderCurrentModeMessage = () => {
    if (isGroupReorderEnabled) {
      return "Groups Reorder Mode";
    }
    if (isComponentsReorderEnabled) {
      return "Components Reorder Mode";
    }
    return "";
  };

  const addNewGroupFooter = () => {
    return (
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => setAddNewGroupDialogVisibility(false)}
          className="p-button-sm p-button-danger"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={() => handleAddNewTabGroup(newGroupTitle)}
          className="p-button-sm p-button-success"
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-row justify-content-between mb-3">
        <div>{dashboard?.title}</div>
        <div className="font-bold text-xl">{renderCurrentModeMessage()}</div>
        <div className="flex flex-row gap-2">
          <div>Options:</div>
          <InputSwitch checked={isEditMode} onChange={e => handleEditMode(e)} />
        </div>
      </div>
      {isEditMode && (
        <div className="flex flex-row justify-content-between">
          <div className="flex flex-column gap-2">
            <div>Tabs</div>
            <div className="flex flex-row gap-2 mb-3">
              <Button
                icon="pi pi-save"
                tooltip="Save dashboard"
                className="p-button-success"
                tooltipOptions={{ position: "bottom" }}
                onClick={() => updateQuery.mutate(dashboard)}
              />
              <Button
                icon="pi pi-plus"
                tooltip="Add new tab"
                className="p-button-success"
                tooltipOptions={{ position: "bottom" }}
                onClick={() => handleAddNewTab(dashboard, setDashboard, [])}
              />
              <Button
                tooltip="Change tab title"
                icon="pi pi-pencil"
                tooltipOptions={{ position: "bottom" }}
                className="p-button-warning"
                onClick={triggerTabTitleChangeDialog}
              />
              <Button
                tooltip="Reorder tabs"
                icon="pi pi-arrow-right-arrow-left"
                className="p-button-secondary rotate-90"
                onClick={() => setReorderTabsDialogVisibility(true)}
                tooltipOptions={{ position: "bottom" }}
              />
              <Button
                icon="pi pi-trash"
                tooltip="Remove tab"
                tooltipOptions={{ position: "bottom" }}
                className="p-button-danger"
                onClick={removeTabConfirmation}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2">
            <div>Import:</div>
            <div className="flex flex-row gap-2">
              <Button
                tooltip="Import dashboard or tab"
                icon="pi pi-file-import"
                tooltipOptions={{ position: "bottom" }}
                className="p-button-success"
                onClick={() => setIsImportDashboardDialogVisibility(true)}
              />
            </div>
          </div>
          <div className="flex flex-column gap-2">
            <div>Tab Groups:</div>
            <div className="flex flex-row gap-2">
              <Button
                tooltip="Add new tab group"
                icon="pi pi-plus"
                className="p-button-success"
                tooltipOptions={{ position: "bottom" }}
                onClick={() => setAddNewGroupDialogVisibility(true)}
              />
              <Button
                tooltip="Reorder tab groups"
                icon="pi pi-arrow-right-arrow-left"
                tooltipOptions={{ position: "bottom" }}
                className={`${isGroupReorderEnabled ? "p-button-warning rotate-90 border" : "p-button-secondary rotate-90"}`}
                onClick={() => setIsGroupReorderEnabled(!isGroupReorderEnabled)}
              />
            </div>
          </div>
        </div>
      )}
      <div>
        <div className="tabsFlex">
          {dashboard?.dashboardTabsJs?.map((tab, index) => (
            <div
              key={tab.uuid}
              className={`tab dashboard-tab ${
                tabId !== undefined && tabId === index ? "tab-selected" : "tab-unselected"
              }`}
              onClick={() => onTabChange(index)}
            >
              {tab.title}
            </div>
          ))}
        </div>
        <div className="mobile-menu">
          <Button
            onClick={event => menuRef.current?.toggle(event)}
            className="p-button-icon-only mb-3 w-12"
          >
            <i className="pi pi-bars mr-2 text-2xl" />
            <div className="font-bold text-xl">Tabs</div>
          </Button>
          <Menu ref={menuRef} model={menuItems} popup className="w-12 menu-list" />
        </div>
      </div>
      {isDialogVisible && (
        <Dialog
          header="Change tab title"
          style={{ width: "20rem", height: "16rem" }}
          modal
          resizable
          draggable
          visible={isDialogVisible}
          onHide={handleHideDialog}
          footer={titleChangeFooter}
        >
          <div>{dashboard.dashboardTabsJs[tabId].title}</div>
          <InputText
            value={selectedTabTitle}
            className="w-full"
            onChange={e => {
              setSelectedTabTitle(e.target.value);
            }}
          />
        </Dialog>
      )}
      {isImportDashboardDialogVisible && (
        <ImportDashboardOrTab
          isImportDashboardDialogVisible={isImportDashboardDialogVisible}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          importNewDashboard={importNewDashboard}
          dashboard={dashboard}
        />
      )}
      {addNewGroupDialogVisibility && (
        <Dialog
          header="Add new tab group"
          className="w-30rem"
          visible={addNewGroupDialogVisibility}
          footer={addNewGroupFooter}
          onHide={() => setAddNewGroupDialogVisibility(false)}
        >
          <div className="flex flex-column gap-2">
            <label htmlFor="groupTitle">Group Title</label>
            <InputText
              value={newGroupTitle}
              className="w-full"
              onChange={e => {
                setNewGroupTitle(e.target.value);
              }}
            />
          </div>
        </Dialog>
      )}
      {reorderTabsDialogVisibility && (
        <Dialog
          header="Reorder tabs"
          className="w-30rem"
          style={{ height: "60rem" }}
          modal
          resizable
          draggable
          visible={reorderTabsDialogVisibility}
          onHide={() => setReorderTabsDialogVisibility(false)}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="tabs" direction="vertical">
              {providedDrop => (
                <div
                  {...providedDrop.droppableProps}
                  ref={providedDrop.innerRef}
                  className="tabsFlex column"
                >
                  {dashboard.dashboardTabsJs.map((tab, index) => (
                    <Draggable key={tab.uuid} draggableId={tab.uuid} index={index}>
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="tab dashboard-tab-reorder"
                          style={provided.draggableProps.style}
                        >
                          {tab.title}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {providedDrop.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Dialog>
      )}
    </>
  );
};

export default DashboardMain;
